import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const RecentlyViewedContext = createContext();

const STORAGE_KEY = 'wasser_recently_viewed';
const MAX_ITEMS = 12;

export const RecentlyViewedProvider = ({ children }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setRecentlyViewed(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Error loading recently viewed:', error);
      setRecentlyViewed([]);
    }
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentlyViewed));
    } catch (error) {
      console.error('Error saving recently viewed:', error);
    }
  }, [recentlyViewed]);

  // Add product to recently viewed
  const addToRecentlyViewed = useCallback((product) => {
    if (!product || !product._id) return;

    setRecentlyViewed((prev) => {
      // Remove if already exists
      const filtered = prev.filter((p) => p._id !== product._id);

      // Create minimal product data to save space
      const minimalProduct = {
        _id: product._id,
        slug: product.slug,
        name:
          typeof product.name === 'object'
            ? product.name
            : { ru: product.name, en: product.name, ky: product.name },
        price: product.price,
        originalPrice: product.originalPrice,
        discount: product.discount,
        images: product.images?.slice(0, 1) || [], // Only first image
        category: product.category,
        inStock: product.inStock,
        viewedAt: Date.now(),
      };

      // Add to beginning, limit to MAX_ITEMS
      return [minimalProduct, ...filtered].slice(0, MAX_ITEMS);
    });
  }, []);

  // Clear all recently viewed
  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Remove specific product
  const removeFromRecentlyViewed = useCallback((productId) => {
    setRecentlyViewed((prev) => prev.filter((p) => p._id !== productId));
  }, []);

  // MEMOIZED value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      recentlyViewed,
      addToRecentlyViewed,
      removeFromRecentlyViewed,
      clearRecentlyViewed,
    }),
    [
      recentlyViewed,
      addToRecentlyViewed,
      removeFromRecentlyViewed,
      clearRecentlyViewed,
    ]
  );

  return (
    <RecentlyViewedContext.Provider value={value}>
      {children}
    </RecentlyViewedContext.Provider>
  );
};

export const useRecentlyViewed = () => {
  const context = useContext(RecentlyViewedContext);
  if (!context) {
    throw new Error(
      'useRecentlyViewed must be used within RecentlyViewedProvider'
    );
  }
  return context;
};

export default RecentlyViewedContext;
